import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import {
  Button,
  Flex,
  Form,
  Heading,
  Modal,
  Paragraph,
  useToast,
} from "@ir/ui";
import { PageHeader } from "@src/presentation/components/Layout/common";
import { useUsersAccessData } from "@src/presentation/providers/UsersAccessDataProvider";
import { SearchInput } from "@src/presentation/components/common";
import { useCallback, useMemo, useState } from "react";
import {
  type UserAddEditFormValues,
  UsersAddEditForm,
} from "./UsersAddEditForm";
import { useUpdateUser } from "./useUpdateUser";
import {
  isUserEmailDuplicateError,
  normalizeUserEmail,
  USER_EMAIL_ALREADY_EXISTS_MESSAGE,
} from "./userEmailValidation";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { Permissions } from "@src/domain/permissions";

export const UsersAccessHeader = () => {
  const {
    users,
    total,
    totalActiveCount,
    loading,
    search,
    setSearch,
    setPage,
    applyUserCreate,
  } = useUsersAccessData();
  const hasSearch = search.trim().length > 0;
  const displayedTotal = total;
  const activeUsersInCurrentItems = users.filter(
    (user) => user.isActive && !user.isDeleted,
  ).length;
  const isAllSearchResultsLoaded = hasSearch && users.length >= total;
  let displayedActiveCount = totalActiveCount;
  if (hasSearch) {
    displayedActiveCount = Math.min(totalActiveCount, total);
  }
  if (isAllSearchResultsLoaded) {
    displayedActiveCount = activeUsersInCurrentItems;
  }
  const activeSuffix = displayedActiveCount === 1 ? "" : "s";
  const subtitle = loading
    ? "Loading users..."
    : `${displayedActiveCount} / ${displayedTotal} Active user${activeSuffix}`;

  const title = "Users & Access";
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFormSubmittable, setIsFormSubmittable] = useState(false);
  const [form] = Form.useForm<UserAddEditFormValues>();
  const { success: successToast, error: errorToast } = useToast();
  const updateUser = useUpdateUser();

  const initialValues = useMemo<UserAddEditFormValues>(
    () => ({
      firstName: "",
      lastName: "",
      email: "",
      isActive: true,
    }),
    [],
  );

  const closeAddUserModal = useCallback(() => {
    setIsAddUserModalOpen(false);
    setIsFormSubmittable(false);
    form.resetFields();
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  const openAddUserModal = useCallback(() => {
    form.resetFields();
    form.setFieldsValue(initialValues);
    setIsFormSubmittable(false);
    setIsAddUserModalOpen(true);
  }, [form, initialValues]);

  const handleSearch = (query: string) => {
    setPage(1);
    setSearch(query);
  };

  const handleAddUserSubmit = async (values: UserAddEditFormValues) => {
    setIsSaving(true);
    try {
      const saved = await updateUser({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: normalizeUserEmail(values.email),
        isActive: Boolean(values.isActive),
      });

      applyUserCreate(saved);
      successToast({ content: "User created successfully." });
      closeAddUserModal();
    } catch (err) {
      if (isUserEmailDuplicateError(err)) {
        form.setFields([
          {
            name: "email",
            errors: [USER_EMAIL_ALREADY_EXISTS_MESSAGE],
          },
        ]);
        setIsFormSubmittable(false);
      } else {
        console.error("Failed to create user", err);
        errorToast({
          content: "Failed to create user. Please try again.",
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ErrorBoundary>
      <PageHeader>
        <Flex align="center" gap={0.5} justifyContent="space-between">
          <Flex direction="column">
            <Heading level={5} className="text-grey-700 font-bold m-0">
              {title}
            </Heading>
            <Paragraph className="text-grey-500 font-regular m-0">
              {subtitle}
            </Paragraph>
          </Flex>
          <Flex
            direction="row"
            align="center"
            gap={0.5}
            justifyContent="space-around"
          >
            <Flex>
              <SearchInput
                onSearch={handleSearch}
                searchQuery={search}
                onClear={() => handleSearch("")}
                placeholder="Search by : name, email, organization"
              />
            </Flex>
            <Flex>
              <HasAnyPermission
                requiredPermissions={[Permissions.USER_MANAGEMENT_ADD]}
              >
                <Button size="small" onPress={openAddUserModal}>
                  Add User
                </Button>
              </HasAnyPermission>
            </Flex>
          </Flex>
        </Flex>
      </PageHeader>
      <Modal
        isOpen={isAddUserModalOpen}
        onClose={closeAddUserModal}
        size="large"
        headerContent={
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Add User
          </Heading>
        }
        footerContent={
          <Flex gap={0.5}>
            <Button
              size="small"
              onPress={closeAddUserModal}
              color="grey"
              isDisabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="primary"
              onPress={() => form.submit()}
              isLoading={isSaving}
              isDisabled={isSaving || !isFormSubmittable}
            >
              Save
            </Button>
          </Flex>
        }
      >
        <UsersAddEditForm
          form={form}
          mode="create"
          initialValues={initialValues}
          onSubmit={handleAddUserSubmit}
          onFormValidityChange={setIsFormSubmittable}
        />
      </Modal>
    </ErrorBoundary>
  );
};
